import { Check, ChevronDown, Layers, Sparkles } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { MODELS, MODEL_BY_ID, modelBadgeStyle } from "@/lib/models";
import { cn } from "@/lib/utils";

interface Props {
  selected: string[];
  onChange: (next: string[]) => void;
  ensemble?: boolean;
  onToggleEnsemble?: () => void;
  compact?: boolean;
}

export function ModelMultiSelect({ selected, onChange, ensemble, onToggleEnsemble, compact }: Props) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const filtered = MODELS.filter(
    (m) => !filter || m.label.toLowerCase().includes(filter.toLowerCase()) || m.vendor.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-1.5" ref={ref}>
      {/* Selected badges */}
      <div className="flex flex-wrap gap-1 items-center">
        {selected.map((id) => {
          const m = MODEL_BY_ID[id];
          if (!m) return null;
          return (
            <button
              key={id}
              onClick={(e) => {
                e.stopPropagation();
                toggle(id);
              }}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded border flex items-center gap-1 hover:opacity-80"
              style={modelBadgeStyle(id)}
              title={`${m.vendor} · $${m.costPer1M}/1M · ${m.contextK}K ctx`}
            >
              {m.label}
              <span className="opacity-60">×</span>
            </button>
          );
        })}
        <button
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "text-[10px] font-mono px-2 py-0.5 rounded border border-dashed border-border hover:border-primary/60 hover:text-primary text-muted-foreground flex items-center gap-1",
          )}
        >
          <Layers className="w-3 h-3" /> {selected.length}/22 models
          <ChevronDown className="w-3 h-3" />
        </button>
        {selected.length > 1 && onToggleEnsemble && (
          <button
            onClick={onToggleEnsemble}
            className={cn(
              "text-[10px] font-mono px-2 py-0.5 rounded border flex items-center gap-1",
              ensemble
                ? "border-secondary/50 bg-secondary/10 text-secondary shadow-glow-violet"
                : "border-border text-muted-foreground hover:text-secondary",
            )}
            title="When enabled, all models run in parallel and a judge picks/merges the best answer."
          >
            <Sparkles className="w-3 h-3" /> {ensemble ? "Ensemble ON" : "Ensemble"}
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className={cn("absolute z-50 mt-1 w-[340px] glass-strong rounded-lg p-2 max-h-[420px] overflow-hidden flex flex-col", compact && "w-[280px]")}>
          <input
            autoFocus
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search 22 models..."
            className="w-full h-8 px-2 mb-2 rounded bg-surface-2 border border-border text-xs outline-none focus:border-primary/60"
          />
          <div className="overflow-y-auto pr-1 space-y-0.5">
            {filtered.map((m) => {
              const on = selected.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggle(m.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-surface-3 text-left",
                    on && "bg-surface-3",
                  )}
                >
                  <span
                    className={cn(
                      "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0",
                      on ? "bg-primary border-primary text-primary-foreground" : "border-border",
                    )}
                  >
                    {on && <Check className="w-2.5 h-2.5" />}
                  </span>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: `hsl(${m.hue} 95% 60%)`, boxShadow: `0 0 6px hsl(${m.hue} 95% 60%)` }}
                  />
                  <span className="flex-1 font-mono">{m.label}</span>
                  <span className="text-[9px] text-muted-foreground">{m.vendor}</span>
                  <span className="text-[9px] text-muted-foreground w-10 text-right">${m.costPer1M}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
